import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { fetchCourseById, fetchMyEnrollments, fetchDemoRequest, checkBatchEnrollment, fetchCurriculumContentClient } from "./clientCourseService";
import DemoRequestModal from "./DemoRequestModal";
import { CourseOfferBanner, isOfferActive } from "./CourseOfferBadge";
import { LEVEL_FALLBACK_IMG } from "./CourseCard";
import "./courses.css";

const fmt = (paise) => `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const LEVEL_LABELS = {
  BEGINNER: "Beginner",
  INTERMEDIATE: "Intermediate",
  PROFESSIONAL: "Professional",
  SPEAK_COMPLETE: "Speak Complete",
};

/* ── batch card ── */
const BatchCard = ({ batch, onEnroll, user, isEnrolledInBatch, enrollmentId, onGoToCourse }) => {
  const isFull = batch.spotsLeft <= 0;

  const handleEnroll = () => {
    if (!user) { onEnroll(null); return; }
    onEnroll(batch);
  };

  const modeLabel = batch.mode === "BATCH" ? "BATCH" : "PERSONAL COACHING";

  return (
    <div className={`cc-batch-card-v2 ${isFull ? "full" : ""}`}>

      {/* ── top mode banner ── */}
      <div className={`cc-batch-banner cc-batch-banner-${batch.mode}`}>
        {modeLabel}
      </div>

      {/* ── card body ── */}
      <div className="cc-batch-body">

        {/* instructor row */}
        <div className="cc-batch-instructor-row">
          <div className="cc-batch-avatar-v2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24, color: "#fff" }}>
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </div>
          <span className="cc-batch-instructor-name-v2">{batch.instructorName || "TBD"}</span>
        </div>

        {/* metadata rows */}
        <div className="cc-batch-meta-v2">
          <div className="cc-batch-meta-row">
            <svg className="cc-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span className="cc-meta-label">Start Date:</span>
            <span className="cc-meta-primary">{(() => {
              const d = new Date(batch.startDate);
              return isNaN(d) ? batch.startDate : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
            })()}</span>
          </div>

          <div className="cc-batch-meta-row">
            <svg className="cc-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="cc-meta-label">Time:</span>
            <span className="cc-meta-primary">{batch.dailySessionTime ? batch.dailySessionTime.slice(0, 5) : ""} <span className="cc-meta-tz">({batch.timezone})</span></span>
          </div>

        </div>
      </div>

      {/* ── enroll footer ── */}
      {isEnrolledInBatch ? (
        <button className="cc-batch-enroll-btn enrolled" onClick={() => onGoToCourse?.(enrollmentId)}>
          ▶ View My Course →
        </button>
      ) : isFull ? (
        <button className="cc-batch-enroll-btn full" disabled>Fully Booked</button>
      ) : (
        <button className="cc-batch-enroll-btn" onClick={handleEnroll}>Enroll Now →</button>
      )}
    </div>
  );
};

const MATERIAL_ICON = { GRAMMAR: "📖", READING: "📄" };
const MATERIAL_NAV  = {
  GRAMMAR: (id) => `/study/grammar/sub-topic/${id}`,
  READING: (id) => `/study/reading/${id}`,
};

/* ── curriculum tab ── */
const CurriculumTab = ({ curriculum, navigate, backUrl }) => (
  <div className="cc-curriculum-list">
    {curriculum.map((lesson) => (
      <div key={lesson.dayNumber} className="cc-lesson-row">
        <div className={`cc-day-badge ${lesson.isAssessment ? "assessment" : ""}`}>
          D{lesson.dayNumber}
          {lesson.isAssessment && <div style={{ fontSize: "0.6rem" }}>★</div>}
        </div>
        <div className="cc-lesson-info">
          <p className="cc-lesson-title">
            {lesson.title}
            {lesson.isAssessment && (
              <span style={{
                marginLeft: 6, fontSize: "0.72rem", background: "#ede9fe",
                color: "#6d28d9", padding: "1px 7px", borderRadius: 999, fontWeight: 700,
              }}>
                Assessment
              </span>
            )}
          </p>
          {lesson.learningObjective && (
            <p className="cc-lesson-obj">{lesson.learningObjective}</p>
          )}
          {lesson.studyMaterials?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.4rem" }}>
              {lesson.studyMaterials.map((m) => (
                <button
                  key={m.id}
                  onClick={() => navigate(MATERIAL_NAV[m.contentType]?.(m.contentId) || "#", { state: { backUrl } })}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: m.contentType === "GRAMMAR" ? "#ede9fe" : "#dbeafe",
                    color: m.contentType === "GRAMMAR" ? "#6d28d9" : "#1d4ed8",
                    border: "none", borderRadius: 999, padding: "3px 10px",
                    fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.75"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  {MATERIAL_ICON[m.contentType]} {m.contentTitle}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="cc-lesson-dur">{lesson.durationMins} min</span>
      </div>
    ))}
  </div>
);

/* ── batches tab ── */
const BatchesTab = ({ batches, onEnroll, user, enrolledBatchMap, onGoToCourse }) => {
  const enrolledBatchIds = new Set(Object.keys(enrolledBatchMap));
  const batchMode    = batches.filter((b) => b.mode === "BATCH");
  const personalMode = batches.filter((b) => b.mode === "PERSONAL");

  if (batches.length === 0) {
    return (
      <div className="cc-empty">
        <div className="cc-empty-icon">📅</div>
        <p>No open batches right now. Check back soon!</p>
      </div>
    );
  }

  return (
    <>
      {batchMode.length > 0 && (
        <div className="cc-batch-section">
          <h3 className="cc-batch-section-title">Batch Sessions</h3>
          <div className="cc-batch-grid">
            {batchMode.map((b) => (
              <BatchCard
                key={b.batchId} batch={b} onEnroll={onEnroll} user={user}
                isEnrolledInBatch={enrolledBatchIds.has(String(b.batchId))}
                enrollmentId={enrolledBatchMap[String(b.batchId)]}
                onGoToCourse={onGoToCourse}
              />
            ))}
          </div>
        </div>
      )}
      {personalMode.length > 0 && (
        <div className="cc-batch-section">
          <h3 className="cc-batch-section-title">Personal Coaching (1-on-1)</h3>
          <div className="cc-batch-grid">
            {personalMode.map((b) => (
              <BatchCard
                key={b.batchId} batch={b} onEnroll={onEnroll} user={user}
                isEnrolledInBatch={enrolledBatchIds.has(String(b.batchId))}
                enrollmentId={enrolledBatchMap[String(b.batchId)]}
                onGoToCourse={onGoToCourse}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const CourseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [showAlreadyEnrolledAlert, setShowAlreadyEnrolledAlert] = useState(
    () => !!location.state?.alreadyEnrolled
  );
  const [demoRequest, setDemoRequest] = useState(null);   // null = not fetched yet, false = none
  // map of batchId -> enrollmentId for enrolled batches
  const [enrolledBatchMap, setEnrolledBatchMap] = useState({});
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    fetchCourseById(id)
      .then((res) => setCourse(res.data))
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [id]);

  // fetch demo request + enrollment status when user is logged in
  useEffect(() => {
    if (!user) return;
    setDemoLoading(true);
    Promise.allSettled([
      fetchDemoRequest(id),
      fetchMyEnrollments(),
    ]).then(([demoRes, enrollRes]) => {
      if (demoRes.status === "fulfilled") {
        setDemoRequest(demoRes.value.data);
      } else {
        setDemoRequest(false);
      }
      if (enrollRes.status === "fulfilled") {
        const enrollments = enrollRes.value.data || [];
        const enrolledMap = {};
        enrollments
          .filter((e) => ["ACTIVE", "PENDING_PAYMENT"].includes(e.status))
          .forEach((e) => { enrolledMap[String(e.batchId)] = e.enrollmentId; });
        setEnrolledBatchMap(enrolledMap);
      }
    }).finally(() => setDemoLoading(false));
  }, [id, user]);

  const handleEnroll = (batch) => {
    if (!batch) { setShowLoginAlert(true); return; }
    if (enrolledBatchMap[String(batch.batchId)]) {
      setShowAlreadyEnrolledAlert(true);
      return;
    }
    navigate(`/courses/${id}/enroll/${batch.batchId}`, { replace: true });
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading course…</p></div>
      </>
    );
  }

  if (!course) {
    return (
      <>
        <Navbar />
        <div className="cc-empty"><div className="cc-empty-icon">❌</div><p>Course not available.</p></div>
      </>
    );
  }

  const enrolledBatchIds = new Set(Object.keys(enrolledBatchMap));
  const enrolledCount = enrolledBatchIds.size;
  const curriculum = course.curriculum || [];
  const batches    = course.availableBatches || [];

  return (
    <>
      <Navbar />
      <div className="cc-page" style={{ paddingTop: 24 }}>

        {/* ── header ── */}
        <div className="cc-detail-header">
          <img
            src={course.thumbnailUrl || LEVEL_FALLBACK_IMG[course.level]}
            alt={course.name}
            className="cc-detail-thumb"
          />

          <div className="cc-detail-meta">
            <div className="cc-detail-chips">
              <span className={`cc-level cc-level-${course.level}`}>
                {LEVEL_LABELS[course.level] || course.level}
              </span>
              <span className="cc-level" style={{ background: "#f0fdf4", color: "#15803d" }}>
                {course.totalDays} days · {course.sessionDurationMins} min/session
              </span>
            </div>

            <h1>{course.name}</h1>

            <div className="cc-detail-prices">
              {course.batchModeEnabled && course.priceBatch != null && (
                <span>
                  Batch:{" "}
                  {isOfferActive(course.offer) ? (
                    <>
                      <strong style={{ color: "#dc2626" }}>{fmt(course.offer.offeredValueBatch)}</strong>
                      {" "}<span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{fmt(course.priceBatch)}</span>
                    </>
                  ) : fmt(course.priceBatch)}
                </span>
              )}
              {course.batchModeEnabled && course.personalModeEnabled && (
                <span style={{ margin: "0 8px", color: "#d1d5db" }}>|</span>
              )}
              {course.personalModeEnabled && course.pricePersonal != null && (
                <span>
                  Personal Coaching:{" "}
                  {isOfferActive(course.offer) ? (
                    <>
                      <strong style={{ color: "#dc2626" }}>{fmt(course.offer.offeredValuePersonal)}</strong>
                      {" "}<span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{fmt(course.pricePersonal)}</span>
                    </>
                  ) : fmt(course.pricePersonal)}
                </span>
              )}
            </div>

            {/* ── demo / enrolled CTA ── */}
            {!demoLoading && course.isActive && (() => {
              if (enrolledCount > 0) {
                return (
                  <span className="cc-level" style={{ background: "#dcfce7", color: "#15803d", marginTop: 10, display: "inline-block" }}>
                    ✓ Enrolled in {enrolledCount} batch{enrolledCount > 1 ? "es" : ""} — see Batches tab
                  </span>
                );
              }
              if (!demoRequest) {
                return (
                  <button
                    className="cc-btn cc-btn-outline"
                    style={{ marginTop: 10 }}
                    onClick={() => {
                      if (!user) { setShowLoginAlert(true); return; }
                      setShowDemoModal(true);
                    }}
                  >
                    🎓 Book a Free Demo
                  </button>
                );
              }
              const s = demoRequest.status;
              if (s === "PENDING") return (
                <span className="cc-level" style={{ background: "#fef9c3", color: "#a16207", marginTop: 10, display: "inline-block" }}>
                  ⏳ Demo Requested — We'll confirm soon
                </span>
              );
              if (s === "SCHEDULED") return (
                <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="cc-level" style={{ background: "#dbeafe", color: "#1d4ed8" }}>📅 Demo Scheduled</span>
                  {demoRequest.zoomJoinUrl && (
                    <a href={demoRequest.zoomJoinUrl} target="_blank" rel="noreferrer" className="cc-btn cc-btn-primary" style={{ padding: "4px 14px", fontSize: "0.82rem" }}>
                      Join Demo
                    </a>
                  )}
                </div>
              );
              if (s === "COMPLETED") return (
                <span className="cc-level" style={{ background: "#f3f4f6", color: "#6b7280", marginTop: 10, display: "inline-block" }}>
                  ✓ Demo Completed
                </span>
              );
              if (s === "CANCELLED") return (
                <div style={{ marginTop: 10 }}>
                  <span className="cc-level" style={{ background: "#fee2e2", color: "#b91c1c" }}>✗ Demo Cancelled</span>
                  <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>
                    Please contact support to request a new demo.
                  </p>
                </div>
              );
              return null;
            })()}
          </div>
        </div>

        {/* ── tabs ── */}
        <div className="cc-tabs">
          {["overview", "curriculum", "batches"].map((tab) => (
            <button
              key={tab}
              className={`cc-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === "curriculum" && curriculum.length > 0 && (
                <span style={{
                  marginLeft: 5, background: "#e0f2fe", color: "#0369a1",
                  borderRadius: 999, padding: "0 6px", fontSize: "0.72rem",
                }}>
                  {curriculum.length}
                </span>
              )}
              {tab === "batches" && batches.length > 0 && (
                <span style={{
                  marginLeft: 5, background: "#dcfce7", color: "#15803d",
                  borderRadius: 999, padding: "0 6px", fontSize: "0.72rem",
                }}>
                  {batches.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── overview tab ── */}
        {activeTab === "overview" && (
          <div style={{ maxWidth: 680 }}>
            <CourseOfferBanner offer={course.offer} course={course} />

            {course.description && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#374151", marginBottom: "0.5rem" }}>
                  About this course
                </h3>
                <p style={{ fontSize: "0.92rem", color: "#4b5563", lineHeight: 1.7, margin: 0 }}>
                  {course.description}
                </p>
              </div>
            )}

            {course.learningObjectives && (
              <div>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#374151", marginBottom: "0.5rem" }}>
                  What you'll learn
                </h3>
                <p style={{ fontSize: "0.92rem", color: "#4b5563", lineHeight: 1.7, margin: 0 }}>
                  {course.learningObjectives}
                </p>
              </div>
            )}

            <div style={{ marginTop: "1.5rem" }}>
              <button
                className="cc-btn cc-btn-primary"
                onClick={() => setActiveTab("batches")}
              >
                See Available Batches →
              </button>
            </div>
          </div>
        )}

        {/* ── curriculum tab ── */}
        {activeTab === "curriculum" && (
          curriculum.length === 0 ? (
            <div className="cc-empty"><p>Curriculum not yet published.</p></div>
          ) : (
            <CurriculumTab curriculum={curriculum} navigate={navigate} backUrl={location.pathname} />
          )
        )}

        {/* ── batches tab ── */}
        {activeTab === "batches" && (
          <BatchesTab
            batches={batches}
            onEnroll={handleEnroll}
            user={user}
            enrolledBatchMap={enrolledBatchMap}
            onGoToCourse={(eid) => navigate(`/my-courses/${eid}`)}
          />
        )}
      </div>

      {/* ── already enrolled alert ── */}
      {showAlreadyEnrolledAlert && (
        <div className="cc-modal-overlay">
          <div className="cc-modal">
            <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>✅</div>
            <h2>Already Enrolled</h2>
            <p>You are already enrolled in this batch. Visit My Courses to access it.</p>
            <div className="cc-modal-actions">
              <button className="cc-btn cc-btn-outline" onClick={() => setShowAlreadyEnrolledAlert(false)}>
                Stay Here
              </button>
              <button className="cc-btn cc-btn-primary" onClick={() => navigate("/my-courses")}>
                Go to My Courses
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── login alert ── */}}
      {showLoginAlert && (
        <div className="cc-modal-overlay">
          <div className="cc-modal">
            <h2>Login Required</h2>
            <p>You need to login to enroll in this course.</p>
            <div className="cc-modal-actions">
              <button className="cc-btn cc-btn-outline" onClick={() => setShowLoginAlert(false)}>
                Dismiss
              </button>
              <button className="cc-btn cc-btn-primary" onClick={() => navigate(`/login?redirect=/courses/${id}`, { replace: true })}>
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── demo modal ── */}
      {showDemoModal && (
        <DemoRequestModal
          courseId={id}
          course={course}
          onSuccess={(data) => {
            setDemoRequest(data);
            setShowDemoModal(false);
          }}
          onClose={() => setShowDemoModal(false)}
        />
      )}
    </>
  );
};

export default CourseDetailPage;
